import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Utility function to check if the current URL is in kiosk mode
 */
export const isKioskMode = (pathname: string): boolean => {
  return pathname.startsWith('/kiosk');
};

/**
 * Utility function to generate kiosk-aware URLs
 * If currently in kiosk mode, it prefixes the URL with /kiosk
 */
export const getKioskAwareUrl = (url: string, currentPathname: string): string => {
  // If we're already in kiosk mode, prefix the new URL with /kiosk
  if (isKioskMode(currentPathname)) {
    // Avoid double prefixing if the URL already starts with /kiosk
    if (url.startsWith('/kiosk')) {
      return url;
    }
    return `/kiosk${url}`;
  }

  // If not in kiosk mode, return the URL as is
  return url;
};

/**
 * Hook to get kiosk-aware URL generation function
 */
export const useKioskAwareUrl = () => {
  const location = useLocation();

  return (url: string) => getKioskAwareUrl(url, location.pathname);
};

/**
 * Hook that provides kiosk-aware navigation
 * Wraps useNavigate to automatically handle kiosk mode prefixing
 */
export const useKioskAwareNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (url: string, options?: { replace?: boolean; state?: any }) => {
    const kioskAwareUrl = getKioskAwareUrl(url, location.pathname);
    return navigate(kioskAwareUrl, options);
  };
};
